import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { T, alpha, toneColors } from "@v0y4ge/jrni-ui";

/**
 * ProgressSpinner — one busy indicator, three presentations, one engine.
 *
 * Use it anywhere an async action is in flight and the wait can exceed a beat:
 * a sign-in that resolves its landing route, a card whose first read is slow, a
 * whole screen that must not be interacted with until data arrives. The engine
 * (timing, labels, announcements) is shared; only the frame changes.
 *
 *  - `inline`  — the ring beside text (a button, a row). Label hidden by default.
 *  - `block`   — replaces a region (a card, a panel) — centred, fills its box.
 *  - `overlay` — fixed, full-viewport, dimmed backdrop, panel with the label.
 *
 * Owns its own MINIMUM display time: once `active` turns on, the indicator stays
 * for `minDisplayMs` even if `active` flips off sooner, so a fast response never
 * flashes a spinner for one frame. Callers only toggle `active`.
 *
 * Past `elapsedHintAfterMs` it adds "Still working… 4s" so a long wait reads as
 * progress, not a hang. `determinate` + `progress` swap the spin for a fill;
 * `steps` + `currentStep` swap the label for "Step 2 of 3: Loading your workspace".
 *
 * Accessibility: the frame is `role="status"` `aria-live="polite"`; the label
 * (or step counter) is ALWAYS in the DOM for assistive tech, visually hidden
 * where `showLabel` is false. Put `aria-busy="true"` on the region being
 * replaced. Honours `prefers-reduced-motion`: the ring stops spinning and pulses
 * gently in place instead. Built on the library's tokens — no new colour or curve.
 */

export type ProgressSpinnerVariant = "inline" | "block" | "overlay";

export interface ProgressSpinnerStep { label: string }

export interface ProgressSpinnerProps {
  /** Which presentation renders. Default "inline". */
  variant?: ProgressSpinnerVariant;
  /** Whether the busy state is currently on. */
  active: boolean;
  /** Announced (and, with `showLabel`, shown) text. Default "Loading". */
  label?: string;
  /** Show the label visually. Default false for inline, true for block/overlay. */
  showLabel?: boolean;
  /** After this many ms, add an elapsed-time hint. Default 2000. 0 disables. */
  elapsedHintAfterMs?: number;
  /** Minimum time shown once triggered, even if `active` flips off sooner. Default 300. */
  minDisplayMs?: number;
  /** Percentage fill instead of an indeterminate spin. */
  determinate?: boolean;
  /** 0–100, read when `determinate`. */
  progress?: number;
  /** Ordered steps; the counter replaces `label` when present. */
  steps?: ProgressSpinnerStep[];
  /** 0-based index into `steps`. */
  currentStep?: number;
  /** Dimmed backdrop behind the overlay. Default true for overlay. */
  backdrop?: boolean;
  /** Ring diameter step. Default: inline "sm", block "md", overlay "lg". */
  size?: "sm" | "md" | "lg";
  /** Optional footer content under the label (a retry action, a cancel). Block/overlay only. */
  children?: ReactNode;
  /** Inline style on the outer frame. */
  style?: CSSProperties;
  testid?: string;
}

const DIAMETER: Record<"sm" | "md" | "lg", number> = { sm: 16, md: 24, lg: 40 };
const STROKE: Record<"sm" | "md" | "lg", number> = { sm: 2, md: 3, lg: 4 };

const VISUALLY_HIDDEN: CSSProperties = {
  position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden",
  clip: "rect(0, 0, 0, 0)", whiteSpace: "nowrap", border: 0,
};

/** True while the indicator should be on screen: `active`, or inside the minimum window after it was. */
function useMinDisplay(active: boolean, minDisplayMs: number): boolean {
  const [shown, setShown] = useState(active);
  const since = useRef<number | null>(active ? Date.now() : null);
  useEffect(() => {
    if (active) { since.current = since.current ?? Date.now(); setShown(true); return; }
    if (since.current === null) { setShown(false); return; }
    const left = minDisplayMs - (Date.now() - since.current);
    if (left <= 0) { since.current = null; setShown(false); return; }
    const id = setTimeout(() => { since.current = null; setShown(false); }, left);
    return () => clearTimeout(id);
  }, [active, minDisplayMs]);
  return shown;
}

/** Whole seconds elapsed since `shown` turned on; 0 when off. Ticks once a second. */
function useElapsed(shown: boolean): number {
  const [s, setS] = useState(0);
  useEffect(() => {
    if (!shown) { setS(0); return; }
    const start = Date.now();
    const id = setInterval(() => setS(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [shown]);
  return s;
}

export function ProgressSpinner({
  variant = "inline", active, label = "Loading", showLabel, elapsedHintAfterMs = 2000, minDisplayMs = 300,
  determinate = false, progress, steps, currentStep = 0, backdrop, size, children, style, testid,
}: ProgressSpinnerProps) {
  const shown = useMinDisplay(active, minDisplayMs);
  const elapsed = useElapsed(shown);
  if (!shown) return null;

  const labelVisible = showLabel ?? variant !== "inline";
  const dia = DIAMETER[size ?? (variant === "inline" ? "sm" : variant === "block" ? "md" : "lg")];
  const stroke = STROKE[size ?? (variant === "inline" ? "sm" : variant === "block" ? "md" : "lg")];
  const fg = toneColors("primary").fg;
  const track = alpha(fg, 20);
  const step = steps && steps.length ? steps[Math.min(Math.max(currentStep, 0), steps.length - 1)] : null;
  const text = step ? `Step ${Math.min(currentStep + 1, steps!.length)} of ${steps!.length}: ${step.label}` : label;
  const hint = elapsedHintAfterMs > 0 && elapsed * 1000 >= elapsedHintAfterMs ? `Still working… ${elapsed}s` : null;
  const pct = determinate ? Math.min(100, Math.max(0, progress ?? 0)) : null;

  const ring = (
    <span
      aria-hidden="true"
      data-jrni-component="progress-spinner-ring"
      data-jrni-determinate={pct !== null ? "true" : undefined}
      style={{
        display: "inline-block", width: dia, height: dia, borderRadius: T.radius.pill, boxSizing: "border-box", flexShrink: 0,
        ...(pct !== null
          ? { background: `conic-gradient(${fg} ${pct}%, ${track} 0)`, transition: "background 240ms ease-out", WebkitMask: `radial-gradient(farthest-side, transparent calc(100% - ${stroke}px), #000 calc(100% - ${stroke}px + 0.5px))`, mask: `radial-gradient(farthest-side, transparent calc(100% - ${stroke}px), #000 calc(100% - ${stroke}px + 0.5px))` }
          : { border: `${stroke}px solid ${track}`, borderTopColor: fg, animation: "jrni-progress-spin 0.8s linear infinite" }),
      }}
    />
  );

  const labelNode = (
    <span style={labelVisible ? { display: "flex", flexDirection: "column", alignItems: variant === "inline" ? "flex-start" : "center", gap: 2, minWidth: 0 } : VISUALLY_HIDDEN}>
      <span style={{ fontFamily: T.font, fontSize: variant === "overlay" ? 15 : 13.5, fontWeight: 600, color: T.n1, lineHeight: 1.4, textAlign: variant === "inline" ? "left" : "center" }}>{text}</span>
      {hint ? <span data-testid={testid ? `${testid}-elapsed` : undefined} style={{ fontFamily: T.font, fontSize: 12.5, color: T.soft, lineHeight: 1.4 }}>{hint}</span> : null}
      {pct !== null && variant !== "inline" ? <span style={{ fontFamily: T.font, fontSize: 12.5, color: T.soft }}>{Math.round(pct)}%</span> : null}
    </span>
  );

  const status = { role: "status" as const, "aria-live": "polite" as const, "data-jrni-component": "progress-spinner", "data-jrni-variant": variant, "data-testid": testid };

  if (variant === "inline") {
    return (
      <span {...status} style={{ display: "inline-flex", alignItems: "center", gap: 8, verticalAlign: "middle", ...style }}>
        {ring}{labelNode}<Keyframes />
      </span>
    );
  }
  if (variant === "block") {
    return (
      <div {...status} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 14, width: "100%", height: "100%", minHeight: 120, padding: 24, boxSizing: "border-box", ...style }}>
        {ring}{labelNode}{children ? <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>{children}</div> : null}<Keyframes />
      </div>
    );
  }
  return (
    <div {...status} style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: (backdrop ?? true) ? "rgba(17,17,20,0.45)" : "transparent", ...style }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 18, minWidth: 260, maxWidth: 360, padding: "32px 28px 28px", borderRadius: T.radius.lg, background: T.white, boxShadow: T.shadowLg, border: `1px solid ${T.border}`, boxSizing: "border-box" }}>
        {ring}{labelNode}{children ? <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap", marginTop: 4 }}>{children}</div> : null}
      </div>
      <Keyframes />
    </div>
  );
}

function Keyframes() {
  return (
    <style>{`
      @keyframes jrni-progress-spin { to { transform: rotate(360deg); } }
      @keyframes jrni-progress-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }
      @media (prefers-reduced-motion: reduce) {
        [data-jrni-component="progress-spinner-ring"]:not([data-jrni-determinate]) { animation: jrni-progress-pulse 1.6s ease-in-out infinite; }
      }
    `}</style>
  );
}
